<template>
  <div class="parchment-delivery-list" :class="{ 'parchment-delivery-list--embedded': embedded }">
    <breadcrumb v-if="!embedded" :items="breadcrumbs"></breadcrumb>

    <component :is="embedded ? 'div' : 'v-container'" :fluid="!embedded">
      <div v-if="!embedded" class="d-flex mb-4 flex-wrap">
        <h2 class="font-weight-bolder">{{ $t("memberDataSection.parchmentDelivery.listTitle") }}</h2>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          outlined
          class="mr-2 mb-2 text-uppercase"
          @click="goBulkImport"
        >
          {{ $t("memberDataSection.parchmentDelivery.bulkImport.importButton") }}
        </v-btn>
        <v-btn
          color="primary"
          dark
          class="mr-2 mb-2 text-uppercase"
          @click="handleAddParchmentDelivery"
        >
          {{ $t("memberDataSection.parchmentDelivery.addParchmentDelivery") }}
        </v-btn>
      </div>

      <component :is="embedded ? 'div' : 'v-card'" elevation="0" :class="embedded ? '' : 'px-5'">
        <component :is="embedded ? 'div' : 'v-card-text'">
          <v-data-table
            ref="listTable"
            v-bind="tableProps"
            v-on="tableListeners"
          >
            <template v-slot:item.idAndOutturn="{ item }">
              <div>{{ item.parchmentDeliveryId || "—" }}</div>
              <div>{{ item.outturnNumber || "—" }}</div>
            </template>

            <template v-slot:item.processingBatchLot="{ item }">
              {{ item.processingBatchLot || "—" }}
            </template>

            <template v-slot:item.netWeightAndCoffeeType="{ item }">
              <div>{{ item.netWeight != null ? `${item.netWeight} kg` : "—" }}</div>
              <div>{{ item.coffeeType || "—" }}</div>
            </template>

            <template v-slot:item.deliveryDate="{ item }">
              {{ formatDeliveryDate(item.deliveryDate) }}
            </template>

            <template v-slot:item.status="{ item }">
              <v-btn
                v-if="isDeliveredStatus(item.status)"
                small
                rounded
                depressed
                min-width="120"
                height="34"
                class="green-button parchment-delivery-list-table__status-btn"
              >
                {{ $t("memberDataSection.parchmentDelivery.statusDelivered") }}
              </v-btn>
              <v-btn
                v-else
                small
                rounded
                depressed
                min-width="120"
                height="34"
                class="gray-button parchment-delivery-list-table__status-btn"
              >
                {{ $t("memberDataSection.parchmentDelivery.statusDraft") }}
              </v-btn>
            </template>

            <template v-slot:item.action="{ item }">
              <v-menu offset-y left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="viewRecord(item)">
                    <v-list-item-title>{{ $t("memberDataSection.view") }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item :disabled="isDeliveredStatus(item.status)" @click="!isDeliveredStatus(item.status) && editRecord(item)">
                    <v-list-item-title :class="isDeliveredStatus(item.status) ? 'grey--text' : ''">{{ $t("memberDataSection.edit") }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="deleteRecord(item)">
                    <v-list-item-title class="red--text">{{ $t("memberDataSection.delete") }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>

            <template v-slot:top>
              <div class="d-flex mb-4 flex-wrap align-center" :class="{ 'mt-2': embedded }">
                <div class="ml-3" style="width: 280px;">
                  <v-text-field
                    v-model="search"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    :placeholder="$t('Search')"
                    hide-details
                    @input="onSearch"
                  ></v-text-field>
                </div>
                <v-spacer></v-spacer>
                <div class="d-flex align-center mb-3">
                  <div class="py-1 title">
                    {{ fromRecord }} - {{ toRecord }} {{ $t("of") }} {{ totalRecords }}
                  </div>
                  <v-btn
                    class="mx-2"
                    fab
                    small
                    outlined
                    color="primary"
                    :disabled="options.page <= 1"
                    @click="pageChange(false)"
                  >
                    <v-icon dark>mdi-chevron-left</v-icon>
                  </v-btn>
                  <v-btn
                    class="mx-2"
                    fab
                    small
                    outlined
                    color="primary"
                    :disabled="options.page >= totalPages"
                    @click="pageChange(true)"
                  >
                    <v-icon dark>mdi-chevron-right</v-icon>
                  </v-btn>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn class="mx-2" fab small outlined color="primary" v-bind="attrs" v-on="on">
                        <v-icon dark>mdi-cog-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t("customization") }}</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn class="mx-2" fab small outlined color="primary" v-bind="attrs" v-on="on">
                        <v-icon dark>mdi-download</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t("download") }}</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn class="mx-2" fab small outlined color="primary" v-bind="attrs" v-on="on">
                        <v-icon dark>mdi-printer</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t("print") }}</span>
                  </v-tooltip>
                </div>
              </div>
            </template>
          </v-data-table>
        </component>
      </component>
    </component>

    <ParchmentDeliveryAddDialog
      v-if="!embedded"
      v-model="addDialogVisible"
      @saved="refresh"
      @close="addDialogVisible = false"
    />

    <ParchmentDeliveryDetailDialog
      v-model="detailDialogVisible"
      :record="viewingRecord"
      @edit="onDetailEdit"
      @close="onDetailClose"
    />

    <ParchmentDeliveryEditDialog
      v-model="editDialogVisible"
      mode="edit"
      :record="editingRecord"
      :buying-station-id="buyingStationId"
      @saved="onEditSaved"
      @close="onEditClose"
    />

    <!-- Delete confirmation dialog -->
    <v-dialog v-model="deleteConfirmVisible" max-width="420" persistent>
      <v-card>
        <v-card-title class="text-h6">{{ $t("memberDataSection.parchmentDelivery.deleteConfirmTitle") }}</v-card-title>
        <v-card-text>{{ $t("memberDataSection.parchmentDelivery.deleteConfirmText") }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelDelete">{{ $t("cancel") }}</v-btn>
          <v-btn color="error" depressed :loading="deleteLoading" @click="confirmDelete">{{ $t("memberDataSection.delete") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ParchmentDeliveryService from "@/_services/ParchmentDeliveryService";
import { formatParchmentDisplayDate } from "@/utils/parchmentDeliveryBulkImport";
import ParchmentDeliveryAddDialog from "./components/AddDialog.vue";
import ParchmentDeliveryEditDialog from "./components/EditDialog.vue";
import ParchmentDeliveryDetailDialog from "./components/DetailDialog.vue";

export default {
  name: "ParchmentDeliveryList",
  components: {
    ParchmentDeliveryAddDialog,
    ParchmentDeliveryEditDialog,
    ParchmentDeliveryDetailDialog,
  },
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
    buyingStationId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      addDialogVisible: false,
      detailDialogVisible: false,
      viewingRecord: null,
      editDialogVisible: false,
      editingRecord: null,
      deleteConfirmVisible: false,
      deleteLoading: false,
      pendingDeleteItem: null,
      search: "",
      debounceTimer: null,
      records: [],
      loading: false,
      options: { page: 1, itemsPerPage: 10, sortBy: [], sortDesc: [] },
      fromRecord: 0,
      toRecord: 0,
      totalRecords: 0,
      totalPages: 1,
    };
  },
  computed: {
    breadcrumbs() {
      return [
        { text: this.$t("dashboard"), disabled: false, to: { name: "AdminDashboard" }, exact: true },
        { text: this.$t("dashboardSection.cropOverview"), disabled: true, href: "#" },
        { text: this.$t("memberDataSection.buyingStation"), disabled: false, to: { name: "CoffeeBuyingStation" }, exact: true },
        { text: this.$t("memberDataSection.parchmentDelivery.listTitle"), disabled: true, href: "#" },
      ];
    },
    headers() {
      const t = (key) => this.$t(`memberDataSection.parchmentDelivery.${key}`);
      return [
        { text: t("parchmentDeliveryIdAndOutturn"), align: "start", value: "idAndOutturn", sortable: false },
        { text: t("processingBatchLot"), align: "start", value: "processingBatchLot", sortable: false },
        { text: t("netWeightAndCoffeeType"), align: "start", value: "netWeightAndCoffeeType", sortable: false },
        { text: t("deliveryDateColumn"), align: "start", value: "deliveryDate", sortable: false },
        { text: this.$t("status"), align: "center", value: "status", sortable: false },
        { align: "center", value: "action", sortable: false, width: "72px" },
      ];
    },
    tableProps() {
      return {
        headers: this.headers,
        items: this.records,
        itemsPerPage: this.options.itemsPerPage,
        footerProps: { "items-per-page-options": [10, 25, 50] },
        hideDefaultFooter: true,
        loading: this.loading,
        options: this.options,
        loadingText: this.$t("loadingData"),
        serverItemsLength: this.totalRecords,
        class: "parchment-delivery-list-table",
      };
    },
    tableListeners() {
      return {
        "update:options": (options) => {
          this.options = options;
        },
      };
    },
  },
  watch: {
    buyingStationId() {
      this.options = { ...this.options, page: 1 };
      this.fetchList();
    },
    options: {
      handler() {
        this.fetchList();
      },
      deep: true,
    },
  },
  mounted() {
    this.fetchList();
  },
  methods: {
    formatDeliveryDate(value) {
      return formatParchmentDisplayDate(value) || "—";
    },
    isDeliveredStatus(status) {
      return String(status || "").toLowerCase() === "delivered";
    },
    goBulkImport() {
      this.$router.push({ name: "CoffeeBuyingStationParchmentDeliveriesBulkImport" });
    },
    handleAddParchmentDelivery() {
      this.addDialogVisible = true;
    },
    viewRecord(item) {
      if (!item) return;
      this.viewingRecord = item;
      this.detailDialogVisible = true;
    },
    onDetailClose() {
      this.viewingRecord = null;
    },
    onDetailEdit(record) {
      this.detailDialogVisible = false;
      this.viewingRecord = null;
      this.editRecord(record);
    },
    editRecord(item) {
      if (!item) return;
      this.editingRecord = this.mapRowToEditRecord(item);
      this.editDialogVisible = true;
    },
    // Map an API row to the structure expected by AddDialog/EditDialog
    mapRowToEditRecord(item) {
      return {
        id: item.id,
        payloadJsonData: {
          id: item.id,
          deliveryDate: item.deliveryDate || null,
          processingBatchIds: (item.processingBatches || []).map((b) => b.id),
          outturnNumber: item.outturnNumber || "",
          season: item.season || "",
          growerName: item.growersName || "",
          growerCode: item.growersCode || "",
          coffeeType: item.coffeeType || "",
          unitSize: item.unitSize != null ? item.unitSize : 60,
          grossWeight: item.grossWeight != null ? item.grossWeight : null,
          netWeightWithBags: item.netWeightWithBag != null ? item.netWeightWithBag : null,
          netWeight: item.netWeight != null ? item.netWeight : null,
          moisture: item.moisture != null ? item.moisture : null,
          totalQuantity: item.totalQuantity != null ? item.totalQuantity : null,
          leftoverHandling:
            item.leftOverOption === "ATTACH_LEFTOVER"
              ? "attach"
              : "management",
        },
      };
    },
    onEditClose() {
      this.editingRecord = null;
    },
    onEditSaved() {
      this.editingRecord = null;
      this.refresh();
    },
    deleteRecord(item) {
      if (!item?.id) return;
      this.pendingDeleteItem = item;
      this.deleteConfirmVisible = true;
    },
    cancelDelete() {
      this.deleteConfirmVisible = false;
      this.pendingDeleteItem = null;
    },
    async confirmDelete() {
      const item = this.pendingDeleteItem;
      if (!item?.id) return;
      this.deleteLoading = true;
      try {
        await ParchmentDeliveryService.deleteParchmentDelivery(item.id);
        this.$notify({
          type: "success",
          title: this.$t("success"),
          text: this.$t("memberDataSection.parchmentDelivery.deleteParchmentDeliverySaved"),
        });
        this.deleteConfirmVisible = false;
        this.pendingDeleteItem = null;
        this.refresh();
      } catch (err) {
        this.$notify({
          type: "error",
          text: err?.data?.message || err?.message || this.$t("memberDataSection.parchmentDelivery.deleteParchmentDeliveryFailed"),
        });
      } finally {
        this.deleteLoading = false;
      }
    },
    onSearch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.options = { ...this.options, page: 1 };
        this.fetchList();
      }, 400);
    },
    pageChange(forward) {
      const page = forward ? this.options.page + 1 : this.options.page - 1;
      this.options = { ...this.options, page };
    },
    refresh() {
      this.fetchList();
    },
    async fetchList() {
      if (!this.buyingStationId) return;
      this.loading = true;
      try {
        const res = await ParchmentDeliveryService.getParchmentDeliveryList(
          this.buyingStationId,
          {
            page: this.options.page,
            limit: this.options.itemsPerPage,
            search: this.search || undefined,
          }
        );
        const pd = res?.data?.parchmentDeliveries || {};
        const rows = (pd.rows || []).map((row) => this.mapApiRow(row));
        this.records = rows;
        this.totalRecords = pd.totalRows ?? 0;
        this.totalPages = Math.ceil(this.totalRecords / this.options.itemsPerPage) || 1;
        this.fromRecord = this.totalRecords
          ? (this.options.page - 1) * this.options.itemsPerPage + 1
          : 0;
        this.toRecord = this.totalRecords
          ? Math.min(this.options.page * this.options.itemsPerPage, this.totalRecords)
          : 0;
      } catch (e) {
        this.records = [];
        this.totalRecords = 0;
        this.fromRecord = 0;
        this.toRecord = 0;
        this.totalPages = 1;
        this.$notify({
          type: "error",
          text: e?.data?.message || e?.message || this.$t("error"),
        });
      } finally {
        this.loading = false;
      }
    },
    // Map API row to display + action shape
    mapApiRow(row) {
      const parchmentDeliveryId = `PD-${String(row.id).padStart(5, "0")}`;
      const batchLot = (row.processingBatches || [])
        .map((b) => b.batchCode || `PB-${String(b.id).padStart(5, "0")}`)
        .filter(Boolean)
        .join(", ");
      return {
        ...row,
        parchmentDeliveryId,
        processingBatchLot: batchLot || "—",
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.parchment-delivery-list-table ::v-deep thead th {
  background-color: #1b5e20 !important;
}

.parchment-delivery-list-table ::v-deep thead th span {
  color: #fff !important;
  text-transform: uppercase;
  font-weight: 600;
}

.parchment-delivery-list-table ::v-deep tbody td {
  font-size: 14px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.4;
}

.parchment-delivery-list-table__status-btn {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
  pointer-events: none;
}

.green-button {
  background-color: #d4edda !important;
  border: 2px solid #28a745 !important;
  color: #28a745 !important;
}

.gray-button {
  background-color: #ffffff !important;
  border: 2px solid #212121 !important;
  color: #212121 !important;
}
</style>
